import { useAuth } from "@clerk/react"
import { ProductType } from "@repo/api-dtos"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronDown, Edit, Gamepad2, Plus } from "lucide-react"
import { useEffect, useState } from "react"
import { z } from "zod"
import { CreateEntityModal } from "@/components/create-content/create-entity-modal"
import { Button } from "@/components/ui/button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { GameBacklogGrid } from "@/features/catalogue-quality/GameBacklogGrid"
import { PricesTab } from "@/features/catalogue-quality/price-stats/PricesTab"
import { StrandedFinishLinesTab } from "@/features/catalogue-quality/StrandedFinishLinesTab"
import { LockedExpansionsNote } from "@/features/expansion-locks/LockedExpansionsNote"
import { UpcomingExpansionsBanner } from "@/features/upcoming-expansions/UpcomingExpansionsBanner"
import { useOrpc } from "@/integrations/orpc"
import { formatGameName } from "@/utils/game.helpers"

const verificationStatusFilter = z.enum([
	"not-verified",
	"in-progress",
	"verified",
	"stale",
])

const searchSchema = z.object({
	verified: z.array(verificationStatusFilter).optional(),
	minCardsTcg: z.number().int().nonnegative().optional(),
	minCardsCm: z.number().int().nonnegative().optional(),
	minCardsImage: z.number().int().nonnegative().optional(),
	minSealedTcg: z.number().int().nonnegative().optional(),
	minSealedCm: z.number().int().nonnegative().optional(),
	minSealedImage: z.number().int().nonnegative().optional(),
	releasedAfter: z.string().datetime().optional(),
	releasedBefore: z.string().datetime().optional(),
	quickFilter: z.string().optional(),
	/** When set, auto-expand the detail row for this expansion + scroll to it. */
	expansion: z.string().optional(),
	tab: z.enum(["backlog", "prices", "printings"]).optional().catch(undefined),
	create: z
		.enum(["expansion", "product-card", "product-sealed"])
		.optional()
		.catch(undefined),
})

export const Route = createFileRoute("/catalogue/games/$gameSlug")({
	component: GameBacklogPage,
	validateSearch: searchSchema,
})

function GameBacklogPage() {
	const { gameSlug } = Route.useParams()
	const search = Route.useSearch()
	const { client, isAuthReady } = useOrpc()
	const { isSignedIn } = useAuth()

	const [createExpansionOpen, setCreateExpansionOpen] = useState(false)
	const [createProductOpen, setCreateProductOpen] = useState(false)
	const [createProductType, setCreateProductType] = useState<ProductType>(
		ProductType.CARD,
	)
	const navigate = Route.useNavigate()

	useEffect(() => {
		if (!search.create) return
		if (search.create === "expansion") {
			setCreateExpansionOpen(true)
		} else {
			setCreateProductType(
				search.create === "product-card"
					? ProductType.CARD
					: ProductType.SEALED,
			)
			setCreateProductOpen(true)
		}
		navigate({
			search: (prev) => ({ ...prev, create: undefined }),
			replace: true,
		})
	}, [search.create, navigate])

	const filters = {
		verified: search.verified,
		minMissingFor: {
			cards: {
				tcgplayer: search.minCardsTcg,
				cardmarket: search.minCardsCm,
				image: search.minCardsImage,
			},
			sealed: {
				tcgplayer: search.minSealedTcg,
				cardmarket: search.minSealedCm,
				image: search.minSealedImage,
			},
		},
		releasedAfter: search.releasedAfter
			? new Date(search.releasedAfter)
			: undefined,
		releasedBefore: search.releasedBefore
			? new Date(search.releasedBefore)
			: undefined,
		quickFilter: search.quickFilter,
	}

	const query = useQuery({
		queryKey: ["catalogueQuality", "getGameBacklog", gameSlug, filters],
		enabled: isAuthReady && !!isSignedIn,
		queryFn: () =>
			client.catalogueQuality.getGameBacklog({
				gameSlug,
				filters,
				sort: {
					field: "releaseDate",
					direction: "desc",
				},
			}),
	})

	if (query.error) {
		const message =
			query.error instanceof Error ? query.error.message : String(query.error)
		return (
			<div className="flex h-[calc(100vh-3rem)] flex-col items-center justify-center p-4 sm:p-6">
				<p className="mb-2 text-destructive">Failed to load backlog</p>
				<p className="text-sm text-muted-foreground">{message}</p>
			</div>
		)
	}

	const rows = query.data ?? []

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col p-4 sm:p-6">
			<div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
				<div className="min-w-0">
					<h1 className="text-2xl font-bold tracking-tight">
						{formatGameName(gameSlug)}
					</h1>
					<p className="text-sm text-muted-foreground">
						{rows.length > 0
							? `${rows.length} expansions`
							: query.isLoading
								? "Loading…"
								: "No expansions found. Make sure coverage has been computed for this game."}
					</p>
					<LockedExpansionsNote gameSlug={gameSlug} className="mt-2" />
				</div>
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						onClick={() => setCreateExpansionOpen(true)}
					>
						<Plus className="h-4 w-4 mr-2" />
						Create Expansion
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline">
								<Plus className="h-4 w-4 mr-2" />
								Create Product
								<ChevronDown className="h-3 w-3 ml-1" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={() => {
									setCreateProductType(ProductType.CARD)
									setCreateProductOpen(true)
								}}
							>
								Single card
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => {
									setCreateProductType(ProductType.SEALED)
									setCreateProductOpen(true)
								}}
							>
								Sealed product
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
					<Link to="/games/$id" params={{ id: gameSlug }}>
						<Button variant="outline">
							<Gamepad2 className="h-4 w-4 mr-2" />
							Game Details
						</Button>
					</Link>
					<Link to="/games/$id/manage" params={{ id: gameSlug }}>
						<Button>
							<Edit className="h-4 w-4 mr-2" />
							Manage Game
						</Button>
					</Link>
				</div>
			</div>

			<CreateEntityModal
				open={createExpansionOpen}
				setOpen={setCreateExpansionOpen}
				entityType="expansion"
				size="md"
				initialGameSlug={gameSlug}
				onSuccess={() => query.refetch()}
			/>
			<CreateEntityModal
				open={createProductOpen}
				setOpen={setCreateProductOpen}
				entityType="product"
				size="md"
				productType={createProductType}
				initialGameSlug={gameSlug}
				onSuccess={() => query.refetch()}
			/>
			<UpcomingExpansionsBanner gameSlug={gameSlug} className="mb-3" />
			<Tabs
				value={search.tab ?? "backlog"}
				onValueChange={(tab) =>
					navigate({
						search: (prev) => ({
							...prev,
							tab: tab === "prices" || tab === "printings" ? tab : undefined,
						}),
						replace: true,
					})
				}
				className="flex min-h-0 flex-1 flex-col"
			>
				<TabsList variant="line">
					<TabsTrigger value="backlog">Backlog</TabsTrigger>
					<TabsTrigger value="prices">Prices</TabsTrigger>
					<TabsTrigger value="printings">Printings</TabsTrigger>
				</TabsList>
				<TabsContent value="backlog" className="mt-3 min-h-0 flex-1">
					<GameBacklogGrid
						gameSlug={gameSlug}
						rows={rows}
						loading={query.isLoading}
						focusExpansionSlug={search.expansion}
					/>
				</TabsContent>
				<TabsContent
					value="prices"
					className="mt-3 min-h-0 flex-1 overflow-auto"
				>
					<PricesTab gameSlug={gameSlug} />
				</TabsContent>
				<TabsContent
					value="printings"
					className="mt-3 min-h-0 flex-1 overflow-auto"
				>
					<StrandedFinishLinesTab gameSlug={gameSlug} />
				</TabsContent>
			</Tabs>
		</div>
	)
}
